// homeSalonOrderSlice.js (WEB VERSION)
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { Baseurl } from "../../config/BaseUrl";

// =====================================================
//                 API CALLS (THUNKS)
// =====================================================

// Create Home Salon Order
export const createHomeSalonOrder = createAsyncThunk(
  "homeSalonOrder/createHomeSalonOrder",
  async (orderData, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${Baseurl}/api/v1/orderhomesalon/new`,
        orderData
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Network Error");
    }
  }
);

// Get Orders by Client ID
export const getHomeSalonOrdersByClientId = createAsyncThunk(
  "homeSalonOrder/getHomeSalonOrdersByClientId",
  async (clientId, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${Baseurl}/api/v1/orderhomesalon/orderbyclientid/${clientId}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Network Error");
    }
  }
);

// Get Last 10 Orders
export const getLast10HomeSalonOrders = createAsyncThunk(
  "homeSalonOrder/getLast10HomeSalonOrders",
  async (clientId, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${Baseurl}/api/v1/orderhomesalon/tenOrderbyclient/${clientId}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Network Error");
    }
  }
);

// Get Single Order
export const getHomeSalonOrderById = createAsyncThunk(
  "homeSalonOrder/getHomeSalonOrderById",
  async (orderId, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${Baseurl}/api/v1/orderhomesalon/singleorderbyclient/${orderId}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Network Error");
    }
  }
);

// Update Order Status
export const updateHomeSalonOrderStatus = createAsyncThunk(
  "homeSalonOrder/updateHomeSalonOrderStatus",
  async ({ orderId, statusData }, { rejectWithValue }) => {
    try {
      const res = await axios.put(
        `${Baseurl}/api/v1/orderhomesalon/updateorder/${orderId}`,
        statusData
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Network Error");
    }
  }
);

// Update by partner
export const updateHomeSalonOrderByPartner = createAsyncThunk(
  "homeSalonOrder/updateHomeSalonOrderByPartner",
  async (acceptData, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${Baseurl}/api/v1/orderhomesalon/order-updateby-partner`,
        acceptData
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Network Error");
    }
  }
);

// Get Orders by status
export const getHomeSalonOrdersByStatus = createAsyncThunk(
  "homeSalonOrder/getHomeSalonOrdersByStatus",
  async (status, { rejectWithValue }) => {
    try {
      const statusMap = {
        1: "/orderrecieved",
        2: "/orderprocessing",
        3: "/orderdispatched",
        4: "/Orderoutfordelivery",
        5: "/Orderdelivered",
        0: "/Ordercancelled",
      };

      const endpoint = statusMap[status] || "/orderrecieved";

      const res = await axios.get(
        `${Baseurl}/api/v1/orderhomesalon${endpoint}`
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Network Error");
    }
  }
);

// =====================================================
//                 INITIAL STATE
// =====================================================

const initialState = {
  homeSalonOrders: [],
  currentHomeSalonOrder: null,
  ordersByStatus: [],

  orderLoading: false,
  createLoading: false,
  updateLoading: false,

  error: null,
  success: false,
};

// =====================================================
//                      SLICE
// =====================================================

const homeSalonOrderSlice = createSlice({
  name: "homeSalonOrder",
  initialState,
  reducers: {
    setHomeSalonOrders(state, action) {
      state.homeSalonOrders = action.payload;
      state.orderLoading = false;
    },

    clearHomeSalonOrders(state) {
      state.homeSalonOrders = [];
      state.currentHomeSalonOrder = null;
      state.ordersByStatus = [];
    },

    clearCurrentHomeSalonOrder(state) {
      state.currentHomeSalonOrder = null;
    },

    clearHomeSalonError(state) {
      state.error = null;
    },

    clearHomeSalonSuccess(state) {
      state.success = false;
    },

    // LOCAL STATUS UPDATE
    updateHomeSalonOrderStatusLocal(state, action) {
      const { orderId, status, statusText } = action.payload;

      const index = state.homeSalonOrders.findIndex((o) => o._id === orderId);

      if (index !== -1) {
        state.homeSalonOrders[index].Status = status;
        state.homeSalonOrders[index].StatusText = statusText;
      }

      if (state.currentHomeSalonOrder?._id === orderId) {
        state.currentHomeSalonOrder.Status = status;
        state.currentHomeSalonOrder.StatusText = statusText;
      }
    },
  },

  // =====================================================
  //               EXTRA REDUCERS (THUNKS)
  // =====================================================
  extraReducers: (builder) => {
    builder
      // CREATE ORDER
      .addCase(createHomeSalonOrder.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(createHomeSalonOrder.fulfilled, (state, action) => {
        state.createLoading = false;
        state.success = true;

        if (action.payload.success && action.payload.order) {
          state.homeSalonOrders.unshift(action.payload.order);
          state.currentHomeSalonOrder = action.payload.order;
        }
      })
      .addCase(createHomeSalonOrder.rejected, (state, action) => {
        state.createLoading = false;
        state.error =
          action.payload?.message || "Failed to create home salon order";
      })

      // GET BY CLIENT ID
      .addCase(getHomeSalonOrdersByClientId.pending, (state) => {
        state.orderLoading = true;
      })
      .addCase(getHomeSalonOrdersByClientId.fulfilled, (state, action) => {
        state.orderLoading = false;
        if (action.payload.success) {
          state.homeSalonOrders = action.payload.order || [];
        }
      })
      .addCase(getHomeSalonOrdersByClientId.rejected, (state, action) => {
        state.orderLoading = false;
        state.error =
          action.payload?.message || "Failed to fetch home salon orders";
      })

      // LAST 10 ORDERS
      .addCase(getLast10HomeSalonOrders.pending, (state) => {
        state.orderLoading = true;
      })
      .addCase(getLast10HomeSalonOrders.fulfilled, (state, action) => {
        state.orderLoading = false;
        if (action.payload.success) {
          state.homeSalonOrders = action.payload.order || [];
        }
      })
      .addCase(getLast10HomeSalonOrders.rejected, (state) => {
        state.orderLoading = false;
      })

      // GET BY ID
      .addCase(getHomeSalonOrderById.pending, (state) => {
        state.orderLoading = true;
      })
      .addCase(getHomeSalonOrderById.fulfilled, (state, action) => {
        state.orderLoading = false;
        if (action.payload.success) {
          state.currentHomeSalonOrder = action.payload.order;
        }
      })
      .addCase(getHomeSalonOrderById.rejected, (state, action) => {
        state.orderLoading = false;
        state.error =
          action.payload?.message || "Failed to fetch order details";
      })

      // UPDATE ORDER STATUS
      .addCase(updateHomeSalonOrderStatus.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
      })
      .addCase(updateHomeSalonOrderStatus.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.success = true;

        if (action.payload.success && action.payload.order) {
          const index = state.homeSalonOrders.findIndex(
            (o) => o._id === action.payload.order._id
          );

          if (index !== -1) {
            state.homeSalonOrders[index] = action.payload.order;
          }

          if (state.currentHomeSalonOrder?._id === action.payload.order._id) {
            state.currentHomeSalonOrder = action.payload.order;
          }
        }
      })
      .addCase(updateHomeSalonOrderStatus.rejected, (state, action) => {
        state.updateLoading = false;
        state.error =
          action.payload?.message || "Failed to update home salon order";
      })

      // UPDATE BY PARTNER
      .addCase(updateHomeSalonOrderByPartner.pending, (state) => {
        state.updateLoading = true;
      })
      .addCase(updateHomeSalonOrderByPartner.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.success = true;

        if (action.payload.success && action.payload.order) {
          const index = state.homeSalonOrders.findIndex(
            (o) => o._id === action.payload.order._id
          );

          if (index !== -1) {
            state.homeSalonOrders[index] = action.payload.order;
          }

          if (state.currentHomeSalonOrder?._id === action.payload.order._id) {
            state.currentHomeSalonOrder = action.payload.order;
          }
        }
      })
      .addCase(updateHomeSalonOrderByPartner.rejected, (state, action) => {
        state.updateLoading = false;
        state.error =
          action.payload?.message || "Failed to update order by partner";
      })

      // GET BY STATUS
      .addCase(getHomeSalonOrdersByStatus.pending, (state) => {
        state.orderLoading = true;
      })
      .addCase(getHomeSalonOrdersByStatus.fulfilled, (state, action) => {
        state.orderLoading = false;
        if (action.payload.success) {
          state.ordersByStatus = action.payload.orders || [];
        }
      })
      .addCase(getHomeSalonOrdersByStatus.rejected, (state, action) => {
        state.orderLoading = false;
        state.error =
          action.payload?.message || "Failed to fetch orders by status";
      });
  },
});

// EXPORT ACTIONS
export const {
  setHomeSalonOrders,
  clearHomeSalonOrders,
  clearCurrentHomeSalonOrder,
  clearHomeSalonError,
  clearHomeSalonSuccess,
  updateHomeSalonOrderStatusLocal,
} = homeSalonOrderSlice.actions;

// EXPORT REDUCER
export default homeSalonOrderSlice.reducer;
